import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const ParallaxSection = ({ image, children }: { image: string; children: React.ReactNode }) => {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);

  return (
    <div className="relative h-screen flex items-center justify-center overflow-hidden">
      <motion.div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('${image}')`,
          y
        }}
      >
        <div className="absolute inset-0 bg-[#9AA581]/60" />
      </motion.div>
      <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
        {children}
      </div>
    </div>
  );
};

const ExpertCard = ({ name, title, image, description }: { 
  name: string;
  title: string;
  image: string;
  description: string;
}) => {
  const getObjectPosition = (name: string) => {
    switch (name) {
      case "Sophie Lagesse Ducray":
        return "object-[center_20%]";
      case "Astrid Koenig":
        return "object-[center_30%]";
      case "Karen Schüller-Lagesse":
        return "object-[center_35%]";
      case "Cathy Armataffet":
        return "object-[center_35%]";
      default:
        return "object-center";
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
      className="group relative"
    >
      <div className="overflow-hidden rounded-lg">
        <img 
          src={image}
          alt={name}
          className={`w-full h-[400px] object-cover transition-transform duration-700 group-hover:scale-110 ${getObjectPosition(name)}`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#9AA581]/90 via-[#9AA581]/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </div>
      <div className="mt-6 space-y-2">
        <h3 className="text-2xl font-serif text-white">{name}</h3>
        <p className="text-[#c9ab81] font-serif italic">{title}</p>
        <p className="text-white/80 leading-relaxed">{description}</p>
      </div>
    </motion.div>
  );
};

const Experts = () => {
  const { t, i18n } = useTranslation();

  const experts = {
    fr: [
      {
        name: "Sophie Lagesse Ducray",
        title: "Coach de Vie",
        image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739903845/Sophie_qv0pgl.jpg",
        description: "Spécialisée en développement personnel, résilience et accompagnement des personnes hypersensibles. Elle vous guide vers un meilleur équilibre émotionnel."
      },
      {
        name: "Astrid Koenig",
        title: "Psychologue",
        image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739903843/Astrid_nmegwx.jpg",
        description: "Pratiquant la thérapie ACT, elle vous accompagne dans l'acceptation de vos émotions et votre développement personnel."
      },
      {
        name: "Arianna Bonazzi",
        title: "Psychologue & Praticienne Reiki",
        image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739903842/Arianna_ygfclz.jpg",
        description: "Spécialisée en psychologie de l'enfance et méditation thérapeutique, elle combine approche psychologique et guérison énergétique."
      },
      {
        name: "Rova Rakotondrahova",
        title: "Professeure de Yoga",
        image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739903844/Rova_lhcniu.jpg",
        description: "Enseignante passionnée, elle transmet les bienfaits du yoga avec douceur et bienveillance."
      },
      {
        name: "Aurélie Fleuriau-Chateau",
        title: "Professeure de Yoga et Méditation",
        image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739903844/Aure%CC%81lie_wmq6rh.jpg",
        description: "Elle combine yoga et méditation pour une approche holistique du bien-être."
      },
      {
        name: "Johanna Armand",
        title: "Praticienne Janzu",
        image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739903843/Johanna_c2zwdr.jpg",
        description: "Experte en méditation aquatique, elle vous guide vers un profond lâcher-prise et une reconnexion à vous-même."
      },
      {
        name: "Karen Schüller-Lagesse",
        title: "Coach Mentale",
        image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739903844/Karen_qdzioq.jpg",
        description: "Elle vous aide à développer une mentalité forte et positive pour atteindre vos objectifs."
      },
      {
        name: "Danila Lacoste",
        title: "Coach de Vie & Conseillère en Image",
        image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739903843/Danila_sxs9wf.jpg",
        description: "Elle vous accompagne dans votre transformation intérieure et extérieure."
      },
      {
        name: "Cathy Armataffet",
        title: "Psychologue Clinicienne",
        image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739903842/Cathy_h7ocjp.jpg",
        description: "Son approche clinique vous aide à comprendre et surmonter vos défis personnels."
      },
      {
        name: "Cindy d'Unienville",
        title: "Art Thérapeute",
        image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739903841/Cindy_rdu2t6.jpg",
        description: "Elle utilise l'art comme outil thérapeutique pour libérer votre créativité et vos émotions."
      },
      {
        name: "Capucine",
        title: "Ambassadrice Aura Immersion",
        image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1743657801/CAPUCINE_PHOTO_SITE_lfax4q.jpg",
        description: "Authentique et lumineuse, elle incarne les valeurs d'Aura avec sa voix sensible et son désir de retour à l'essentiel. Elle inspire les femmes à se reconnecter à leur véritable essence."
      },
      {
        name: "Marie Durand",
        title: "Fondatrice",
        image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739903842/Marie_Durand_aaz8nr.jpg",
        description: "Visionnaire et créatrice d'Aura Immersion, Marie a conçu ces retraites transformatrices pour accompagner les femmes vers leur plein potentiel."
      }
    ],
    en: [
      {
        name: "Sophie Lagesse Ducray",
        title: "Life Coach",
        image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739903845/Sophie_qv0pgl.jpg",
        description: "Specialized in personal development, resilience, and guidance for highly sensitive people. She guides you towards better emotional balance."
      },
      {
        name: "Astrid Koenig",
        title: "Psychologist",
        image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739903843/Astrid_nmegwx.jpg",
        description: "Practicing ACT therapy, she accompanies you in accepting your emotions and personal development."
      },
      {
        name: "Arianna Bonazzi",
        title: "Psychologist & Reiki Practitioner",
        image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739903842/Arianna_ygfclz.jpg",
        description: "Specialized in child psychology and therapeutic meditation, she combines psychological approach with energy healing."
      },
      {
        name: "Rova Rakotondrahova",
        title: "Yoga Teacher",
        image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739903844/Rova_lhcniu.jpg",
        description: "A passionate teacher who shares the benefits of yoga with gentleness and care."
      },
      {
        name: "Aurélie Fleuriau-Chateau",
        title: "Yoga and Meditation Teacher",
        image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739903844/Aure%CC%81lie_wmq6rh.jpg",
        description: "She combines yoga and meditation for a holistic approach to well-being."
      },
      {
        name: "Johanna Armand",
        title: "Janzu Practitioner",
        image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739903843/Johanna_c2zwdr.jpg",
        description: "Expert in aquatic meditation, she guides you towards deep letting go and self-reconnection."
      },
      {
        name: "Karen Schüller-Lagesse",
        title: "Mental Coach",
        image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739903844/Karen_qdzioq.jpg",
        description: "She helps you develop a strong and positive mindset to achieve your goals."
      },
      {
        name: "Danila Lacoste",
        title: "Life Coach & Image Consultant",
        image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739903843/Danila_sxs9wf.jpg",
        description: "She accompanies you in your inner and outer transformation."
      },
      {
        name: "Cathy Armataffet",
        title: "Clinical Psychologist",
        image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739903842/Cathy_h7ocjp.jpg",
        description: "Her clinical approach helps you understand and overcome your personal challenges."
      },
      {
        name: "Cindy d'Unienville",
        title: "Art Therapist",
        image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739903841/Cindy_rdu2t6.jpg",
        description: "She uses art as a therapeutic tool to release your creativity and emotions."
      },
      {
        name: "Capucine",
        title: "Aura Immersion Ambassador",
        image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1743657801/CAPUCINE_PHOTO_SITE_lfax4q.jpg",
        description: "Authentic and radiant, she embodies Aura's values with her sensitive voice and desire to return to the essential. She inspires women to reconnect with their true essence."
      },
      {
        name: "Marie Durand",
        title: "Founder",
        image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739903842/Marie_Durand_aaz8nr.jpg",
        description: "Visionary and creator of Aura Immersion, Marie designed these transformative retreats to guide women towards their full potential."
      }
    ]
  };

  const currentExperts = experts[i18n.language as keyof typeof experts] || experts.fr;

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <ParallaxSection image="https://res.cloudinary.com/dansvy9a5/image/upload/v1739380762/IMG_3207_2_cq4cx5.jpg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-7xl font-serif mb-6 text-white">
            {t('experts.hero.title')} <span className="text-[#c9ab81] italic">{t('experts.hero.subtitle')}</span>
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto font-light">
            {t('experts.hero.description')}
          </p>
        </motion.div>
      </ParallaxSection>

      {/* Impact Statement Section */}
      <div className="py-32 relative overflow-hidden section-transition">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(154,165,129,0.05)_0%,rgba(0,0,0,0)_70%)]" />
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <h2 className="text-5xl font-serif text-white leading-tight">
                {t('experts.impact.title')}
                <br />
                <span className="text-[#c9ab81] italic">{t('experts.impact.subtitle')}</span>
                <br />
                {t('experts.impact.subtitle2')}
                <br />
                <span className="text-[#c9ab81] italic">{t('experts.impact.subtitle3')}</span>
              </h2>
              <div className="space-y-6">
                <p className="text-xl text-white/80 leading-relaxed">
                  {t('experts.impact.description1')}
                </p>
                <p className="text-xl text-white/80 leading-relaxed">
                  {t('experts.impact.description2')}
                </p>
              </div>
            </motion.div>
            <div className="relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1 }}
                className="relative z-10 aspect-[3/4] rounded-lg overflow-hidden"
              >
                <img 
                  src="https://images.unsplash.com/photo-1545205597-3d9d02c29597?q=80&w=2940&auto=format&fit=crop"
                  alt="Espace de méditation et bien-être"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#9AA581]/30 to-transparent" />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 1.2 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: 0.3 }}
                className="absolute -top-10 -right-10 -bottom-10 -left-10 border border-[#c9ab81] rounded-lg"
              />
            </div>
          </div>

          {/* Key Impact Numbers */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mt-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center space-y-4"
            >
              <div className="text-6xl font-serif text-[#c9ab81]">{t('experts.stats.experience.number')}</div>
              <p className="text-white/80">{t('experts.stats.experience.text')}</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-center space-y-4"
            >
              <div className="text-6xl font-serif text-[#c9ab81]">{t('experts.stats.women.number')}</div>
              <p className="text-white/80">{t('experts.stats.women.text')}</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-center space-y-4"
            >
              <div className="text-6xl font-serif text-[#c9ab81]">{t('experts.stats.satisfaction.number')}</div>
              <p className="text-white/80">{t('experts.stats.satisfaction.text')}</p>
            </motion.div>
          </div>

          {/* Testimonial */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="max-w-4xl mx-auto text-center mt-32"
          >
            <div className="text-8xl font-serif text-[#c9ab81] opacity-20 mb-6">"</div>
            <p className="text-2xl font-serif text-white italic leading-relaxed mb-8">
              {t('experts.quote.text')}
            </p>
            <div className="text-[#c9ab81]">{t('experts.quote.author')}</div>
          </motion.div>
        </div>
      </div>

      {/* Experts Grid */}
      <div className="max-w-7xl mx-auto px-4 pb-32">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
          {currentExperts.map((expert, index) => (
            <ExpertCard key={index} {...expert} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Experts;